import { Button, Divider, Grid, Paper, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import SearchField from 'src/shared/components/form/searchField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { GiftDataGrid } from 'src/modules/gift/components/giftDataGrid';
import GiftTabs from 'src/modules/gift/components/giftTabs';
import { useTranslation } from 'react-i18next';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { AddBox, Download, IndeterminateCheckBox } from '@mui/icons-material';
import { AdvancedSearch } from 'src/shared/components/organisms/advancedSearch';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { yupResolver } from '@hookform/resolvers/yup';
import { AdvancedSearchChipSummary, ChipSpecs } from 'src/shared/components/organisms/advancedSearchChipSummary';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { CurrencyEnum } from 'src/shared/models/enums/currency.enum';
import { FundraiserPicker } from '../components/fundraiserPicker';
import { GiftSearchFilter } from '../models/giftSearchFilter';
import { giftSearchSchema } from '../schemas.ts/giftSearchSchema';
import { GiftAdvancedSearchSection } from '../components/giftAdvancedSearchSection';
import { DonorAdvancedSearchSection } from '../components/donorAdvancedSearchSection';

export const GiftsListPage = () => {
  const [, setLoading] = useState(false);
  const { t } = useTranslation(['gift']);
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();

  const currency = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.Currency, CurrencyEnum.SEK);
  const [formExpanded, setFormExpanded] = useLocalStorage<boolean>('giftSearchFormExpanded', true);
  const [startExport, setStartExport] = useState<boolean>(false);
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [giftSearchFilter, setGiftSearchFilter] = useLocalStorage(LocalStorageKeys.GiftSearchFilter, {
    isHandled: null,
  } as unknown as GiftSearchFilter);

  const schema = giftSearchSchema(t);
  type DeletableField = 'dateFrom' | 'dateTo' | 'minAmount' | 'maxAmount' | 'donorName' | 'donorEmail' | 'honoredName';

  const formMethods = useForm({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      fundraiserId: 0,
      searchString: '',
      isHandled: null,
      dateFrom: giftSearchFilter?.dateFrom ?? '',
      dateTo: giftSearchFilter?.dateTo ?? '',
      minAmount: giftSearchFilter?.minAmount ?? '',
      maxAmount: giftSearchFilter?.maxAmount ?? '',
      donorName: giftSearchFilter?.donorName ?? '',
      donorEmail: giftSearchFilter?.donorEmail ?? '',
      honoredName: giftSearchFilter?.honoredName ?? '',
    },
  });

  const { handleSubmit, setValue, watch } = formMethods;

  const searchFilter: GiftSearchFilter = useMemo(() => {
    return {
      fundraiserId: giftSearchFilter?.fundraiserId ?? undefined,
      searchString: giftSearchFilter?.searchString,
      isHandled: giftSearchFilter?.isHandled !== undefined ? giftSearchFilter?.isHandled : null,
      dateFrom: giftSearchFilter?.dateFrom ?? '',
      dateTo: giftSearchFilter?.dateTo ?? '',
      minAmount: giftSearchFilter?.minAmount ?? '',
      maxAmount: giftSearchFilter?.maxAmount ?? '',
      donorName: giftSearchFilter?.donorName ?? '',
      donorEmail: giftSearchFilter?.donorEmail ?? '',
      honoredName: giftSearchFilter?.honoredName ?? '',
    } as unknown as GiftSearchFilter;
  }, [
    giftSearchFilter?.fundraiserId,
    giftSearchFilter?.searchString,
    giftSearchFilter?.isHandled,
    giftSearchFilter?.dateFrom,
    giftSearchFilter?.dateTo,
    giftSearchFilter?.minAmount,
    giftSearchFilter?.maxAmount,
    giftSearchFilter?.donorName,
    giftSearchFilter?.donorEmail,
    giftSearchFilter?.honoredName,
  ]);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setGiftSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setGiftSearchFilter((prev) => ({ ...prev, ...{ searchString: tempSearchString } }));
      } else {
        setGiftSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
    }
  }, [isMounted, searchInitialized, setGiftSearchFilter, tempSearchString]);

  const loadingCallback = useCallback((isLoading: boolean) => {
    setLoading(isLoading);
  }, []);

  const onSearch = (data) => {
    const toNumOrNull = (v) => (v === '' || v == null ? undefined : Number(v));

    const minNr = toNumOrNull(data.minAmount);
    const maxNr = toNumOrNull(data.maxAmount);

    const [minOut, maxOut] = minNr != null && maxNr != null && minNr > maxNr ? [maxNr, minNr] : [minNr, maxNr];

    setValue('minAmount', minOut ?? '');
    setValue('maxAmount', maxOut ?? '');

    const applied: GiftSearchFilter = {
      fundraiserId: searchFilter.fundraiserId ?? undefined,
      searchString: searchFilter.searchString,
      isHandled: searchFilter.isHandled,
      dateFrom: DateHelper.getDate(data.dateFrom),
      dateTo: DateHelper.getDate(data.dateTo),
      minAmount: minOut,
      maxAmount: maxOut,
      donorEmail: data.donorEmail,
      donorName: data.donorName,
      honoredName: data.honoredName,
    };
    setGiftSearchFilter(applied);
  };

  const fields = watch();

  const chips: ChipSpecs<DeletableField>[] = useMemo(() => {
    const chipLayout: ChipSpecs<DeletableField>[] = [];

    if (fields?.dateFrom) {
      chipLayout.push({
        field: 'dateFrom',
        title: `${t('gift:dateFrom')} `,
        label: `${DateHelper.getDate(fields.dateFrom)}`,
      });
    }
    if (fields?.dateTo) {
      chipLayout.push({
        field: 'dateTo',
        title: `${t('gift:dateTo')} `,
        label: `${DateHelper.getDate(fields.dateTo)}`,
      });
    }
    if (fields?.minAmount != null && Number(fields.minAmount) > 0) {
      chipLayout.push({
        field: 'minAmount',
        title: `${t('gift:amountFrom')} `,
        label: `${fields.minAmount.toString()}`,
        currency,
      });
    }
    if (fields?.maxAmount != null && Number(fields.maxAmount) > 0) {
      chipLayout.push({
        field: 'maxAmount',
        title: `${t('gift:amountTo')} `,
        label: `${fields.maxAmount}`,
        currency,
      });
    }
    if (fields?.donorName) {
      chipLayout.push({
        field: 'donorName',
        title: `${t('gift:donor')} `,
        label: `${fields.donorName}`,
      });
    }
    if (fields?.donorEmail) {
      chipLayout.push({
        field: 'donorEmail',
        title: `${t('gift:donorEmail')} `,
        label: `${fields.donorEmail}`,
      });
    }
    if (fields?.honoredName) {
      chipLayout.push({
        field: 'honoredName',
        title: `${t('gift:honored')} `,
        label: `${fields.honoredName}`,
      });
    }

    return chipLayout;
  }, [currency, fields.dateFrom, fields.dateTo, fields.donorEmail, fields.donorName, fields.honoredName, fields.maxAmount, fields.minAmount, t]);

  const FORM_EMPTY: Record<DeletableField, string> = {
    dateFrom: '',
    dateTo: '',
    minAmount: '',
    maxAmount: '',
    donorEmail: '',
    donorName: '',
    honoredName: '',
  };

  const APPLIED_EMPTY: Record<DeletableField, undefined> = {
    dateFrom: undefined,
    dateTo: undefined,
    minAmount: undefined,
    maxAmount: undefined,
    donorEmail: undefined,
    donorName: undefined,
    honoredName: undefined,
  };

  const handleResetAll = () => {
    (Object.keys(FORM_EMPTY) as DeletableField[]).forEach((field) => setValue(field, FORM_EMPTY[field]));

    setGiftSearchFilter({
      fundraiserId: undefined,
      searchString: '',
      isHandled: null,
      dateFrom: APPLIED_EMPTY.dateFrom,
      dateTo: APPLIED_EMPTY.dateTo,
      minAmount: APPLIED_EMPTY.minAmount,
      maxAmount: APPLIED_EMPTY.maxAmount,
      donorEmail: APPLIED_EMPTY.donorEmail,
      donorName: APPLIED_EMPTY.donorName,
      honoredName: APPLIED_EMPTY.honoredName,
    });
  };

  const deleteField = (field: DeletableField) => {
    setValue(field, FORM_EMPTY[field]);
    setGiftSearchFilter((prev) => ({ ...prev, [field]: APPLIED_EMPTY[field] }));
  };

  return (
    <GridWrapper>
      <Grid item xs={12}>
        <MainContentHeader title={t('gift:listGiftHeader')} />
      </Grid>
      <Grid item xs={12}>
        <Paper>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSearch)}>
              <PageFilterSection
                leftContent={
                  <Grid container>
                    <>
                      <SearchField
                        initialValue={giftSearchFilter?.searchString || undefined}
                        onSearch={(newSearchString) => {
                          setSearchInitialized(true);
                          setTempSearchString(newSearchString);
                        }}
                      />
                      <Button
                        onClick={() => setFormExpanded((prev) => !prev)}
                        sx={{ ml: { xs: -1, sm: 2 }, mt: { xs: 1, sm: 0 }, justifyContent: 'left', mr: 2, display: 'flex' }}
                      >
                        <Typography variant='body1' sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {!formExpanded && <AddBox fontSize='small' />}
                          {formExpanded && <IndeterminateCheckBox fontSize='small' />}
                          {formExpanded && <span>{t('common:button.hideForm')}</span>}
                          {!formExpanded && <span>{t('common:button.showForm')}</span>}
                        </Typography>
                      </Button>
                    </>
                  </Grid>
                }
                rightContent={
                  <FundraiserPicker
                    label={t('gift:paymentDetail.fundraiser')}
                    name='fundraiserId'
                    initialValue={Number(searchFilter.fundraiserId)}
                    callback={(val) => {
                      ConsoleHelper.log('FundraiserPicker', val);
                      setGiftSearchFilter((prev) => ({ ...prev, ...{ fundraiserId: val } }));
                    }}
                  />
                }
              />
              <Grid item xs={12} mx={2} mt={2} mb={formExpanded ? 0 : 2}>
                <AdvancedSearchChipSummary<DeletableField> chips={chips} onRemove={(val) => deleteField(val)} onClearAll={() => handleResetAll()} />
              </Grid>
              <AdvancedSearch
                formExpanded={formExpanded}
                title={t('gift:gift')}
                content={<GiftAdvancedSearchSection />}
                resetCallback={() => ConsoleHelper.log()}
                showButtonActions={false}
              />
              <AdvancedSearch
                gridSpacing={2}
                formExpanded={formExpanded}
                title={t('gift:donor')}
                content={<DonorAdvancedSearchSection />}
                resetCallback={handleResetAll}
              />
              {formExpanded && <Divider />}
              <Grid container justifyContent='space-between'>
                <Grid item xs='auto'>
                  <GiftTabs
                    initialValue={searchFilter.isHandled}
                    callback={(val) => setGiftSearchFilter((prev) => ({ ...prev, ...{ isHandled: val } }))}
                  />
                </Grid>
                <Grid item xs='auto' alignContent='center' mr={2}>
                  <Button
                    variant='outlined'
                    disableElevation
                    size='small'
                    endIcon={<Download fontSize='small' />}
                    onClick={() => {
                      setStartExport(true);
                    }}
                  >
                    {t('common:button.export')}
                  </Button>
                </Grid>
              </Grid>
              <Divider />
              <GiftDataGrid loadingCallback={loadingCallback} searchFilter={searchFilter} startExport={startExport} setStartExport={setStartExport} />
            </form>
          </FormProvider>
        </Paper>
      </Grid>
    </GridWrapper>
  );
};
